<template>
	<JBox>
		<slot :vueApp="vueApp" />
	</JBox>
</template>

<script>
import { deepClone } from "@/utils"
import compileEditorAstToVueTemplate from "../../compilers/compileEditorAstToVueTemplate"
import transpileEditorScriptToVueObject from "../../compilers/transpileEditorScriptToVueObject"
import provider from "@/dependencyInjections/provider"
import Vue from "vue"

export default {
	props: {
		/**
		 * When in preview mode, it doesn't show selections, hover states etc.
		 */
		mode: {
			type: String,
			default: "editor"
		}
	},

	data() {
		return {
			vueApp: null,
			componentsCache: {}
		}
	},

	methods: {
		initStore() {
			// It will prepare config file for store but it will not instantiate the store, that is happening in Canvas
			const baseComponentsVariants = JSON.parse(this.$store.state.uiEditor?.app?.baseComponentsVariantsJsonString ?? "{}")

			// TODO: bring mutations and actions from CoverQuote
			return {
				strict: true,
				// NOTE: copying mutations from CoverQuote global store
				mutations: {
					SET_LOCALE(state, payload) {
						state.locale = payload
					},
					UPSERT_PROPOSAL(state, payload) {
						state.proposal = { ...state.proposal, ...payload }
					},

					ADD_MESSAGE(state, payload) {
						state.messages = [...state.messages, payload]
					},

					REMOVE_MESSAGE(state, payload) {
						if (payload) {
							state.messages.splice(state.messages.indexOf(payload), 1)
						} else state.messages.splice(0, 1)
					},

					FLUSH_PROPOSAL(state) {
						state.proposal = {}
					}
				},
				// NOTE: copying actions from CoverQuote global store
				actions: {
					switchLocale({ commit }, payload) {
						commit("SET_LOCALE", payload)
					},
					upsertProposal({ commit }, payload) {
						commit("UPSERT_PROPOSAL", payload)
						return Promise.resolve()
					},

					addMessage({ commit }, { message, delay = 10000, preventAutoClose = false }) {
						commit("ADD_MESSAGE", message)

						if (preventAutoClose === false) {
							const timeout = setTimeout(message => {
								commit("REMOVE_MESSAGE", message)
								clearTimeout(timeout)
							}, delay)
						}
					}
				},
				// NOTE: copying state from CoverQuote global store and making some adjustments
				state() {
					return {
						// Current locale
						locale: null,

						// Allowed locales
						allowedLocales: [],

						// HACK: testing tenant, should come from prop
						// TenantId, clientId and base URL
						tenant: "aia_uat",
						client: null,
						baseUrl: null,

						// Tenant settings and generated theme
						settings: {
							baseComponentsVariants
						},
						theme: null,

						// Auth token
						// HACK: using token of studio
						// that's probaly incorrect, we should use token that the app would use e.g. guest user
						token: window.localStorage.getItem(process.env.VUE_APP_TOKEN_KEY),
						tokenExpiry: null,

						// Proposal where we can store data of user
						proposal: {},

						// logged in user information for client portal
						me: {},
						permissions: [],

						// cross route messages for notifications
						messages: []
					}
				},
				getters: {
					// Get component variants
					variants: state => (componentName, variant) => {
						return (
							state?.settings?.baseComponentsVariants?.[componentName]?.[variant] || {
								el: {}
							}
						)
					}
				}
			}
		},

		initRouter() {
			// It will prepare config file for router but it will not instantiate the router, that is happening in Canvas
			return {
				mode: "history",
				routes: []
			}
		},

		memoizedInitComponents() {
			// For the first time it will build all components in store app.components and creates local cache
			// Every consecutive rerender will compile only selected component based on state.selectedComponentId
			let componentsToCompile = {}
			let compiledComponents = {}

			// Get all from store
			deepClone(this.$store.state.uiEditor?.app?.components ?? []).forEach(comp => {
				componentsToCompile[comp.name] = { ...comp }
			})

			// If there is cache, compile only currently selected component
			if (Object.keys(this.componentsCache)?.length) {
				const selectedComponent = this.$store.getters["uiEditor/selectedComponent"]
				componentsToCompile = { [selectedComponent?.name]: { ...selectedComponent } }
				compiledComponents = { ...this.componentsCache, ...this.compileComponents(componentsToCompile) }
			} else compiledComponents = this.compileComponents(componentsToCompile)

			// Update cache
			this.componentsCache = { ...compiledComponents }

			// Return
			return compiledComponents
		},

		compileComponents(componentsToCompile = {}) {
			if (!componentsToCompile) return {}
			let output = {}

			Object.keys(componentsToCompile)?.map(key => {
				const component = componentsToCompile?.[key]

				// Template
				const template = compileEditorAstToVueTemplate({
					editorAst: component?.editorAst ?? [],
					version: component?.version?.version
				})

				// Script
				const script = transpileEditorScriptToVueObject({
					editorScript: component?.editorScript,
					version: component?.version?.version,
					omitProperties: []
				})

				// Return vue component object ready for registration
				// e.g. `{ MyComponent: {template:"", data(){}... } }`
				output[component?.name ?? "UnnamedComponent"] = { template, ...script }
			})

			return output
		},

		render() {
			console.time("Render canvas")

			// Prepare some important bits
			const store = this.initStore()
			const injections = provider()
			const components = this.memoizedInitComponents()
			const router = this.initRouter()

			// Get currently selected component
			const selectedComponentName = this.$store.getters["uiEditor/selectedComponent"]?.name
			if (!selectedComponentName) return // If no component selected, don't continue

			// Register components globally
			// Vue does component registration globally and components rendered in iFrame will be accessible
			Object.keys(components).forEach(compName => {
				Vue.component(compName, { ...components[compName] }) // as a copy to make sure the new component will be re-regisrtered
			})

			// NOTE: Mixins
			// We will need to figure out how to handle mixins
			// It should either be globally registered or somehow registered within the component (maybe const MyMixin outside of component object, inside mixins: [MyMixin])

			// This is global registration and it seems to work,
			// problem is we don't know what mixins we have, we could technically rely on the name of the component that it includes "mixin" string,
			// but it could break. Better would be to scan all components scripts and build a dictionary of mixins from mixins property on each component.
			// For that we probably need script js ast so that it is easier to traverse it
			// const mixin = {
			// 	methods: {
			// 		_cases() {
			// 			console.log("Testing mixin _api")
			// 			return Promise.resolve({ isSuccess: true, res: {} })
			// 		},
			// 		_individuals() {
			// 			console.log("Testing mixin _api")
			// 			return Promise.resolve({ isSuccess: true, res: {} })
			// 		}
			// 	}
			// }
			// Vue.mixin(mixin)

			this.vueApp = {
				store,
				router,
				provide: { ...injections },
				template: `<${selectedComponentName}/>`
			}

			console.timeEnd("Render canvas")
		}
	},

	/**
	 * Rerende when something changes in state.app.components or when we select dfferent component
	 */
	watch: {
		"$store.state.uiEditor.app.components": {
			handler() {
				this.render()
			},
			immediate: false,
			deep: true
		},

		"$store.state.uiEditor.selectedComponentId": {
			handler() {
				this.render()
			},
			immediate: false
		}
	}
}
</script>
